import { Box3, BoxGeometry, Group, Mesh, MeshStandardMaterial, PlaneGeometry, Scene, Texture, Vector2, Vector3 } from 'three';
import { extendShaderWithWorldGradient } from '../utils/shader_utils';
import BoxWithBottomOrigin from '../utils/box_bottom_origin';
import AssetManager from '../utils/asset_manager';
import PropsManager from '../utils/props_manager';

class PondFilter {
  
  private  visible: boolean = false;
  private plants: Mesh[] = [];
  private group:Group;
  private pondGround: BoxWithBottomOrigin
  private pondSurface: Mesh;
  readonly fixedLength = 1; // Fixed length of the pool beach
  private color: number;
  private propsManager: PropsManager
  constructor(private poolLength: number, private poolWidth: number, private poolDepth: number, scene: Scene) {
    this.propsManager = new PropsManager(scene);
    this.group = new Group();
    scene.add(this.group);
  }

  private updatePosition() {
    // Position it just below the water surface and at the end of the pool
    this.pondGround.getMesh().position.set((-this.poolLength + this.fixedLength) / 2, -this.poolDepth, 0);
    this.pondSurface.position.set((-this.poolLength + this.fixedLength) / 2, .005, 0);
  }

  addToScene(scene: Scene) {
    this.group.add(this.pondGround.getMesh());
    this.group.add(this.pondSurface);
    for (const plant of this.plants) {
      this.group.add(plant);
    }
  }

  updateColor(newColor?: number) {
    if (newColor != null) this.color = newColor;
    const material = new MeshStandardMaterial({
      color: this.color, metalness: 1.0,
      roughness: 1.0,
    });
    extendShaderWithWorldGradient(material, 0);
    this.pondGround.getMesh().material = material;
  }

  getRandomPositionOnPondGround(bounding: Box3) {
    // get position on the pond ground using vertices

    // transform bounding box to world coordinates


    const x = Math.random() * (bounding.max.x - bounding.min.x) + bounding.min.x;
    const z = Math.random() * (bounding.max.y - bounding.min.y) + bounding.min.y;
    return new Vector3(x, 0, z);
  }

  buildPlants() {
    this.pondSurface.geometry.computeBoundingBox();
    const bounding = this.pondSurface.geometry.boundingBox;
    // convert to world space
    bounding.min.add(this.pondSurface.position);
    bounding.max.add(this.pondSurface.position);


    const plant1Mesh = AssetManager.getInstance().getAsset("plant1") as Mesh
    const plant2Mesh = AssetManager.getInstance().getAsset("plant2") as Mesh

    // generate a set of plants inside the pond rectangle
    const minScale = 0.8;
    const maxScale = 1.3;

    const plant1Material = new MeshStandardMaterial({
      color: "#83A86C",
      map: AssetManager.getInstance().getAsset("plant1_color") as Texture,
      normalMap: AssetManager.getInstance().getAsset("plant1_normal") as Texture,
      roughnessMap: AssetManager.getInstance().getAsset("plant1_rough") as Texture,
      toneMapped: true,
      metalness: 0,
      envMapIntensity: 0.5,
      normalScale: new Vector2(2, 2),
      side: 2,
    });

    const plant2Material = new MeshStandardMaterial({
      color: "#83A86C",
      map: AssetManager.getInstance().getAsset("plant2_color") as Texture,
      normalMap: AssetManager.getInstance().getAsset("plant2_normal") as Texture,
      roughnessMap: AssetManager.getInstance().getAsset("plant2_rough") as Texture,
      toneMapped: true,
      metalness: 0,
      envMapIntensity: 0.5,
      normalScale: new Vector2(2, 2),
      side: 2,
    })

    // Call the helper function, passing in the existing plants array
    this.createPlant(plant2Mesh, 0.008, plant2Material, bounding, minScale, maxScale, Math.floor((this.poolWidth * 20)), this.plants);
    this.createPlant(plant1Mesh, 0.004, plant1Material, bounding, minScale, maxScale, Math.floor((this.poolWidth * 3)), this.plants);
  }

  private createPlant(
    plantMesh: Mesh,
    initialScale: number,
    material: MeshStandardMaterial,
    bounding: Box3,
    minScale: number,
    maxScale: number,
    numPlants: number,
    existingPlants: Mesh[] // Existing plants array
  ): void {
    for (let i = 0; i < numPlants; i++) {
      const scale = Math.random() * (maxScale - minScale) + minScale;
      const mesh = plantMesh.clone();
      const oScale = initialScale;
      const pos = this.getRandomPositionOnPondGround(bounding);
      mesh.scale.set(oScale * scale, oScale * scale, oScale * scale);
      mesh.position.set(pos.x, 0, pos.z);
      mesh.rotation.set(0, Math.random() * Math.PI * 2.0, 0);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.traverse((child) => {
        if ((child as Mesh).isMesh) {
          const m = child as Mesh;
          m.material = material;
          m.castShadow = true;
          m.receiveShadow = true;
        }
      });
      mesh.layers.set(2);
      existingPlants.push(mesh);  // Add the new mesh to the existing plants array
    }
  }

  resize(poolLength: number, poolWidth: number, poolDepth: number, scene: Scene) {
    // Remove the old mesh from the scene
    if (this.pondGround != null) this.pondGround.getMesh().parent.remove(this.pondGround.getMesh());
    if (this.pondSurface != null) this.pondSurface.parent.remove(this.pondSurface);
    if (this.plants.length > 0) {
      for (const plant of this.plants) {
        plant.parent.remove(plant);
     //   plant.geometry.dispose();
       // plant.parent.remove(plant);
      }

      this.plants = [];
    }

    // Update the dimensions
    this.poolLength = poolLength;
    this.poolWidth = poolWidth;
    this.poolDepth = poolDepth;

    const groundColor = AssetManager.getInstance().getAsset('mud_leaves_color') as Texture;
    groundColor.repeat.set(2, 4);
    const groundNormal = AssetManager.getInstance().getAsset('mud_leaves_normal') as Texture;
    groundNormal.repeat.set(2, 4);
    const roughnessMap = AssetManager.getInstance().getAsset('mud_leaves_rough') as Texture;
    roughnessMap.repeat.set(2, 4);

    // Create a new mesh with the updated dimensions
    this.pondGround = new BoxWithBottomOrigin(this.fixedLength, this.poolDepth, this.poolWidth);

    this.pondSurface = new Mesh(
      new PlaneGeometry(this.fixedLength, this.poolWidth, this.poolWidth),
      new MeshStandardMaterial({
        color: 0x555555,
        map: groundColor,
        toneMapped: true,
        normalMap: groundNormal,
        roughness: 0.8,
        envMapIntensity: 1,
        metalness: 0.0,
      })
    );

    this.pondSurface.geometry.computeBoundingBox();
    this.pondSurface.rotateX(-Math.PI / 2);
    this.pondSurface.receiveShadow = true;

    this.pondGround.getMesh().castShadow = false;
    this.pondGround.getMesh().receiveShadow = true;

    this.pondGround.getMesh().layers.set(2);

    this.updatePosition();  // Update position
    this.updateColor();
    this.buildPlants();
    // Add the mesh back to the scene
    this.group.add(this.pondGround.getMesh());
    this.group.add(this.pondSurface);
    for (const plant of this.plants) {
      this.group.add(plant);
    }
  }

  setVisible(visible: boolean) {
    this.visible = visible;
    this.group.visible = visible;
  }
}

export default PondFilter;
